import { formatarDinheiro } from '@/nucleo/dinheiro';
import type { StatusVariant } from '@/ui/StatusBadge';

/**
 * Tipos, catálogo e utilitários compartilhados entre a lista, o detalhe e os
 * modais de Cobranças DAMSP. Ver `../_dev/cenarioDeTela.ts` para a exceção de
 * dado simulado que autoriza `dados-simulados.json`.
 */

export type SituacaoCobranca = 'PENDENTE' | 'PAGA' | 'VENCIDA' | 'CANCELADA';

export interface Cobranca {
  id: string;
  numeroDamsp: string;
  credenciado: string;
  credenciadoDocumento: string;
  tipoServico: string;
  descricao?: string;
  /** Valor do banco, numeric(14,2) como string ("145.30") — nunca número cru. */
  valor: string;
  situacao: SituacaoCobranca;
  emitidaEm: string;
  vencimento: string;
  pagoEm?: string | null;
  formaPagamento?: 'PIX' | 'BOLETO' | null;
  canceladaEm?: string | null;
  pixCopiaECola?: string | null;
  linhaDigitavel?: string | null;
}

export const SITUACAO_PARA_BADGE: Record<SituacaoCobranca, StatusVariant> = {
  PENDENTE: 'pendente',
  PAGA: 'pago',
  VENCIDA: 'bloqueado',
  CANCELADA: 'cancelado',
};

export const SITUACAO_LABEL: Record<SituacaoCobranca, string> = {
  PENDENTE: 'Pendente',
  PAGA: 'Paga',
  VENCIDA: 'Vencida',
  CANCELADA: 'Cancelada',
};

/** Mesmo conjunto do tipo enumerado `tipo_servico_administrativo` do protótipo. */
export const TIPOS_SERVICO_ADMINISTRATIVO = [
  'SUBSTITUICAO_CONDUTOR',
  'SUBSTITUICAO_MONITOR',
  'SUBSTITUICAO_VEICULO',
  'REVISAO_BANCADA',
  'VISTORIA_CRECHE',
  'TROCA_DRE',
  'INCLUSAO_VEICULO',
  'RECURSO_ADMINISTRATIVO',
  'TROCA_TRIPLA',
] as const;

/** "SUBSTITUICAO_CONDUTOR" -> "SUBSTITUICAO CONDUTOR" — sem title-case, igual ao protótipo. */
export function rotuloTipoServico(valor: string): string {
  return valor.replace(/_/g, ' ');
}

/**
 * Preço público vigente por tipo de serviço administrativo — mostrado como
 * dica no formulário de nova cobrança (protótipo `CobrancaForm.tsx`), igual à
 * tabela `precos_publicos` do banco. Sem rota própria ainda (mesma exceção de
 * dado simulado do resto do módulo financeiro): valores fictícios, nunca
 * apurados com a SMT.
 */
const PRECO_PUBLICO_VIGENTE: Partial<
  Record<(typeof TIPOS_SERVICO_ADMINISTRATIVO)[number], string>
> = {
  SUBSTITUICAO_CONDUTOR: '128.40',
  SUBSTITUICAO_MONITOR: '128.40',
  SUBSTITUICAO_VEICULO: '256.80',
  REVISAO_BANCADA: '412.00',
  VISTORIA_CRECHE: '198.50',
  RECURSO_ADMINISTRATIVO: '86.20',
};

/** Preço público vigente do tipo de serviço, ou `null` quando não há tabela para ele. */
export function precoPublicoVigente(tipoServico: string): string | null {
  return PRECO_PUBLICO_VIGENTE[tipoServico as keyof typeof PRECO_PUBLICO_VIGENTE] ?? null;
}

/** Data no formato "yyyy-mm-dd" ou ISO completo -> "dd/mm/aaaa". */
export function formatarDataBr(valor?: string | null): string {
  if (!valor) return '—';
  const data = new Date(valor.length === 10 ? `${valor}T00:00:00` : valor);
  if (Number.isNaN(data.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(data);
}

/** ISO completo -> "dd/mm/aaaa às hh:mm". */
export function formatarDataHoraBr(valor?: string | null): string {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  const dataFormatada = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(data);
  const horaFormatada = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(data);
  return `${dataFormatada} às ${horaFormatada}`;
}

/** Identificador local, só para a cobrança recém-criada no modal (sem API ainda). */
export function gerarIdLocal(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function gerarNumeroDamsp(): string {
  const ano = new Date().getFullYear();
  const sufixo = String(Math.floor(Math.random() * 900000) + 100000);
  return `DAMSP-${ano}-${sufixo}`;
}

/** Códigos de pagamento mock — nunca um PIX ou boleto de verdade. */
export function gerarPixCopiaEColaMock(): string {
  return `00020126580014BR.GOV.BCB.PIX0136mock-${Math.random().toString(36).slice(2, 10)}5204000053039865406000005802BR5913SMT TEG DAMSP6009SAO PAULO6304MOCK`;
}

export function gerarLinhaDigitavelMock(): string {
  const bloco = () => String(Math.floor(Math.random() * 100_000_000_000)).padStart(11, '0');
  return `${bloco()} ${bloco()} ${bloco()} 1 ${String(Date.now()).slice(-14)}`;
}

export interface EtapaCobranca {
  titulo: string;
  data?: string | null;
  descricao?: string;
  estado: 'concluida' | 'atual' | 'atencao' | 'erro' | 'pendente';
}

/**
 * Marcos da cobrança DAMSP: Emitida → Aguardando pagamento → Paga / Vencida /
 * Cancelada. Constrói a timeline de progresso mostrada no detalhe.
 */
export function construirEtapasCobranca(cobranca: Cobranca): EtapaCobranca[] {
  const {
    situacao,
    emitidaEm,
    vencimento,
    pagoEm,
    canceladaEm,
    formaPagamento,
    numeroDamsp,
    valor,
  } = cobranca;

  const hoje = new Date();
  const dataVencimento = new Date(`${vencimento}T00:00:00`);
  const venceuAgora = situacao === 'PENDENTE' && dataVencimento.getTime() < hoje.getTime();

  const etapas: EtapaCobranca[] = [
    {
      titulo: 'Cobrança emitida',
      data: emitidaEm,
      descricao: `${numeroDamsp} · ${formatarDinheiro(valor)}`,
      estado: 'concluida',
    },
    {
      titulo: 'Aguardando pagamento',
      data: vencimento,
      descricao: `Vencimento em ${formatarDataBr(vencimento)}`,
      estado:
        situacao === 'PAGA' || situacao === 'CANCELADA'
          ? 'concluida'
          : venceuAgora || situacao === 'VENCIDA'
            ? 'atencao'
            : 'atual',
    },
  ];

  if (situacao === 'PAGA') {
    etapas.push({
      titulo: 'Pagamento confirmado',
      data: pagoEm,
      descricao: formaPagamento
        ? `Pago via ${formaPagamento === 'PIX' ? 'PIX' : 'boleto'}`
        : 'Cobrança liquidada',
      estado: 'concluida',
    });
  } else if (situacao === 'VENCIDA') {
    etapas.push({
      titulo: 'Cobrança vencida',
      data: vencimento,
      descricao: 'Aguardando quitação ou renegociação com o credenciado',
      estado: 'erro',
    });
  } else if (situacao === 'CANCELADA') {
    etapas.push({
      titulo: 'Cobrança cancelada',
      data: canceladaEm,
      descricao: 'Cobrança encerrada sem pagamento',
      estado: 'erro',
    });
  } else {
    etapas.push({
      titulo: 'Liquidação',
      estado: 'pendente',
      descricao: 'Aguardando confirmação do pagamento',
    });
  }

  return etapas;
}
